import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../components/api/axiosInstance";
import { getAuthItem } from "../utils/authStorage";
import {
  FaBan,
  FaCheck,
  FaChevronLeft,
  FaChevronRight,
  FaExternalLinkAlt,
  FaEye,
  FaImages,
  FaPaperPlane,
  FaPlus,
  FaRegCalendarAlt,
  FaTimes,
  FaUndo,
} from "react-icons/fa";
import { mapCustomerListItemFromApi, getTenantCompanyLabel } from "../utils/formatters";
import { MediaPicker } from "./dm/components/MediaGallery";
import { errorMessage, portalDmApi, staffDmApi } from "./dm/dmApi";
import { Modal, usePrompt } from "./work/WorkUi";
import { statusClass } from "./MyDay";
import "../assets/css/MyDay.css";
import "./dm/dm.css";

const CONTENT_TYPES = ["Text", "Image", "Video", "Reel", "Carousel", "Story", "Link"];

const action = (status, label, Icon, tone = "", short = label) => ({ status, label, Icon, tone, short });
const SEND_FOR_REVIEW = action("Review", "Send for review", FaEye, "", "Review");
const APPROVE = action("Approved", "Approve", FaCheck, "approve");
const MARK_POSTED = action("Posted", "Mark posted", FaPaperPlane, "post", "Posted");
const SKIP = action("Skipped", "Skip this post", FaBan, "skip", "Skip");
const SEND_FOR_CHANGES = action("Draft", "Send back for changes", FaUndo, "changes", "Changes");
const RESTORE = action("Draft", "Restore to draft", FaUndo);

/** Next steps per current status; the first one is shown as the main button. */
const STATUS_ACTIONS = {
  idea: [SEND_FOR_REVIEW, APPROVE, SKIP],
  draft: [SEND_FOR_REVIEW, APPROVE, SKIP],
  review: [APPROVE, SEND_FOR_CHANGES, SKIP],
  approved: [MARK_POSTED, SEND_FOR_CHANGES, SKIP],
  scheduled: [MARK_POSTED, SEND_FOR_CHANGES, SKIP],
  posted: [],
  skipped: [RESTORE],
};
const DEFAULT_ACTIONS = [SEND_FOR_REVIEW, APPROVE, MARK_POSTED, SKIP];

const PLATFORM_COLORS = {
  facebook: "#1877f2",
  instagram: "#d62976",
  linkedin: "#0a66c2",
  youtube: "#ff0000",
  twitter: "#0f172a",
  x: "#0f172a",
  gmb: "#ea4335",
  google: "#ea4335",
  website: "#0d9488",
  whatsapp: "#25d366",
  pinterest: "#e60023",
};

function platformColor(name) {
  const key = String(name || "").trim().toLowerCase();
  if (PLATFORM_COLORS[key]) return PLATFORM_COLORS[key];
  const match = Object.keys(PLATFORM_COLORS).find((k) => k.length > 2 && key.includes(k));
  return match ? PLATFORM_COLORS[match] : "#64748b";
}

function toISODateLocal(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function mondayOf(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day);
  return x;
}

function addDays(d, n) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() + n);
  return x;
}

function formatWeekRange(start) {
  const end = addDays(start, 6);
  const sameYear = start.getFullYear() === end.getFullYear();
  const startText = start.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  });
  const endText = end.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  return `${startText} – ${endText}`;
}

function getLoggedInUserId() {
  try {
    const user = JSON.parse(getAuthItem("user") || "{}");
    return user.user_id ?? user.id ?? user.User_Id ?? "";
  } catch {
    return "";
  }
}

function emptyForm(weekStartISO, portal = false) {
  return {
    tenant_id: "",
    Dm_Project_Id: "",
    Social_Account_Id: "",
    Planned_Date: weekStartISO || toISODateLocal(),
    Planned_Time: "",
    Content_Type: "Image",
    Caption: "",
    Media_URL: "",
    External_Link: "",
    Assigned_To: portal ? "" : String(getLoggedInUserId() || ""),
    Status_Id: "",
    Notes: "",
  };
}

const RECIPIENT_HINTS = {
  content: "Prepared the content for this post.",
  work_item: "Assigned to the work item for this post.",
  post: "Assigned to this post.",
};

/** Latest "Changes requested by X: note" line the server appends to a post's Notes. */
function latestChangeRequest(notes) {
  const lines = String(notes || "").split("\n").reverse();
  for (const line of lines) {
    const match = line.match(/^\[[^\]]+\] Changes requested by (.+?): (.+)$/);
    if (match) return { by: match[1], note: match[2] };
  }
  return null;
}

function userOption(u) {
  const id = String(u.user_id ?? u.User_Id ?? "");
  return { id, name: [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email || `User #${id}` };
}

/** Ask what needs changing and who should do it, then send the post back to Draft. */
function SendBackDialog({ post, users, loadRecipient, onCancel, onSend }) {
  const [recipient, setRecipient] = useState(null);
  const [assignTo, setAssignTo] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    loadRecipient(post)
      .then((r) => {
        if (cancelled) return;
        setRecipient(r || {});
        if (r?.User_Id) setAssignTo(String(r.User_Id));
      })
      .catch(() => {
        if (!cancelled) setRecipient({});
      });
    return () => {
      cancelled = true;
    };
  }, [post, loadRecipient]);

  const options = useMemo(() => {
    const list = users.map(userOption).filter((o) => o.id);
    if (recipient?.User_Id && !list.some((o) => o.id === String(recipient.User_Id))) {
      list.unshift({ id: String(recipient.User_Id), name: recipient.Name || `User #${recipient.User_Id}` });
    }
    return list;
  }, [users, recipient]);

  const isDefault = recipient?.User_Id && assignTo === String(recipient.User_Id);
  const canSend = note.trim() && assignTo && !saving;

  const submit = async (e) => {
    e?.preventDefault();
    if (!canSend) return;
    setSaving(true);
    setError("");
    try {
      await onSend(post, { note: note.trim(), assign_to: Number(assignTo) });
    } catch (err) {
      setError(errorMessage(err, "Could not send this post back"));
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Send back for changes"
      onClose={saving ? undefined : onCancel}
      footer={
        <>
          <button type="button" className="dmp-btn dmp-btn--ghost wk-btn-lg" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="dmp-btn wk-btn-lg" onClick={submit} disabled={!canSend}>
            {saving ? "Sending…" : "Send back"}
          </button>
        </>
      }
    >
      <form onSubmit={submit}>
        <p className="dm-sendback__post">
          {post.Platform || "Post"} · {post.Planned_Date}
          {post.Caption ? ` — ${post.Caption}` : ""}
        </p>
        <label className="wk-field">
          What needs changing? *
          <textarea
            rows={4}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Use the new logo, shorten the caption, fix the price on the poster"
            maxLength={1000}
          />
        </label>
        <label className="wk-field">
          Send to *
          <select value={assignTo} onChange={(e) => setAssignTo(e.target.value)} disabled={recipient === null}>
            <option value="">{recipient === null ? "Finding the content developer…" : "Pick a person"}</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
                {recipient?.User_Id && o.id === String(recipient.User_Id) ? " (content developer)" : ""}
              </option>
            ))}
          </select>
          <span className="wk-field__hint">
            {isDefault && RECIPIENT_HINTS[recipient.Source]
              ? RECIPIENT_HINTS[recipient.Source]
              : "They get a notification and an email with your note."}
          </span>
        </label>
        {error ? <div className="dm-post-chip__error" role="alert">{error}</div> : null}
      </form>
    </Modal>
  );
}

/** Same counts as /dm-posts/week-summary, for the portal's own tenant. */
function summarizePosts(rows) {
  if (!rows.length) return null;
  const card = { tenant_id: rows[0].tenant_id, Client_Name: rows[0].Client_Name, planned: 0, posted: 0, open: 0 };
  for (const p of rows) {
    card.planned += 1;
    const status = String(p.Status || "").toLowerCase();
    if (status === "posted") card.posted += 1;
    else if (status !== "skipped") card.open += 1;
  }
  return { clients: [card] };
}

/**
 * Week planner for social posts. Staff pick a client; portal users (portal=true) see only
 * their own company's posts and act on them through /client/dm.
 */
function DMCalendar({
  title = "Digital Marketing",
  subtitle = null,
  portal = false,
}) {
  const dmApi = portal ? portalDmApi : staffDmApi;
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [posts, setPosts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [socialAccounts, setSocialAccounts] = useState([]);
  const [filterTenant, setFilterTenant] = useState("");
  const [filterProject, setFilterProject] = useState("");
  const [dmProjects, setDmProjects] = useState([]);
  const [includeInactiveTenants, setIncludeInactiveTenants] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(() => emptyForm(toISODateLocal(mondayOf(new Date())), portal));
  const [saving, setSaving] = useState(false);
  const [pickingMedia, setPickingMedia] = useState(false);
  const [promptUi, askPrompt] = usePrompt();
  const [busyPostId, setBusyPostId] = useState(null);
  const [postErrors, setPostErrors] = useState({});
  const [sendBackPost, setSendBackPost] = useState(null);

  const weekStartISO = toISODateLocal(weekStart);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const loadPortal = useCallback(async () => {
    const params = { date_from: weekStartISO, date_to: toISODateLocal(addDays(weekStart, 6)) };
    if (filterProject) params.dm_project_id = filterProject;
    const [rows, meta] = await Promise.all([portalDmApi.listCalendarPosts(params), portalDmApi.meta()]);
    const list = Array.isArray(rows) ? rows : [];
    const postStatuses = Array.isArray(meta?.post_statuses) ? meta.post_statuses : [];
    setPosts(list);
    setSummary(summarizePosts(list));
    setStatuses(postStatuses);
    setUsers((meta?.users || []).map((u) => ({ user_id: u.User_Id, first_name: u.Name })));
    const draft = postStatuses.find((s) => String(s.Status).toLowerCase() === "draft");
    setForm((prev) => ({
      ...prev,
      Status_Id: prev.Status_Id || (draft ? String(draft.Status_Id) : ""),
      Planned_Date: prev.Planned_Date || weekStartISO,
    }));
  }, [weekStart, weekStartISO, filterProject]);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) {
      setLoading(true);
      setError("");
    }
    if (portal) {
      try {
        await loadPortal();
      } catch (err) {
        console.error(err);
        if (!quiet) setError(errorMessage(err, "Failed to load DM calendar"));
      } finally {
        setLoading(false);
      }
      return;
    }
    try {
      const weekEnd = toISODateLocal(addDays(weekStart, 6));
      const params = { date_from: weekStartISO, date_to: weekEnd };
      if (filterTenant) params.tenant_id = filterTenant;
      if (filterProject) params.dm_project_id = filterProject;
      const tenantParams = includeInactiveTenants
        ? { include_inactive: "1" }
        : { status: "Active" };
      const skipped = Promise.resolve({ data: null });
      const [postsRes, summaryRes, statusRes, tenantsRes, usersRes] = await Promise.all([
        filterTenant ? axiosInstance.get("/dm-posts", { params }) : skipped,
        filterTenant
          ? axiosInstance.get("/dm-posts/week-summary", { params: { week_start: weekStartISO, tenant_id: filterTenant } })
          : skipped,
        axiosInstance.get("/dm-post-statuses"),
        axiosInstance.get("/clients", { params: tenantParams }),
        axiosInstance.get("/users", { params: { status: "Active" } }),
      ]);
      setPosts(Array.isArray(postsRes.data) ? postsRes.data : []);
      setSummary(summaryRes.data || null);
      setStatuses(Array.isArray(statusRes.data) ? statusRes.data : []);
      const rawTenants = Array.isArray(tenantsRes.data)
        ? tenantsRes.data
        : Array.isArray(tenantsRes.data?.data)
          ? tenantsRes.data.data
          : [];
      const mapped = rawTenants.map(mapCustomerListItemFromApi).filter(Boolean);
      // Client-side guard: Active only unless include-inactive is on
      const nextTenants = includeInactiveTenants
        ? mapped
        : mapped.filter((row) => String(row.Status || row.status || "").trim().toLowerCase() === "active");
      setTenants(nextTenants);
      if (filterTenant) {
        const stillVisible = nextTenants.some(
          (row) => String(row.id ?? row.client_id ?? "") === String(filterTenant)
        );
        if (!stillVisible) setFilterTenant("");
      }
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      const draft = (statusRes.data || []).find((s) => String(s.Status).toLowerCase() === "draft");
      setForm((prev) => ({
        ...prev,
        Status_Id: prev.Status_Id || (draft ? String(draft.Status_Id) : ""),
        Planned_Date: prev.Planned_Date || weekStartISO,
      }));
    } catch (err) {
      console.error(err);
      if (!quiet) setError(err.response?.data?.error || "Failed to load DM calendar");
    } finally {
      setLoading(false);
    }
  }, [portal, loadPortal, weekStart, weekStartISO, filterTenant, filterProject, includeInactiveTenants]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    dmApi
      .listProjects()
      .then((rows) => {
        if (!cancelled) setDmProjects(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setDmProjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, [dmApi]);

  const projectsForTenant = useCallback(
    (tenantId) => (tenantId ? dmProjects.filter((p) => String(p.tenant_id) === String(tenantId)) : dmProjects),
    [dmProjects]
  );

  useEffect(() => {
    if (filterProject && !projectsForTenant(filterTenant).some((p) => String(p.Dm_Project_Id) === filterProject)) {
      setFilterProject("");
    }
  }, [filterTenant, filterProject, projectsForTenant]);

  useEffect(() => {
    let cancelled = false;
    const loadSocial = async () => {
      if (!portal && !form.tenant_id) {
        setSocialAccounts([]);
        return;
      }
      try {
        const res = portal
          ? await axiosInstance.get("/client/social-accounts")
          : await axiosInstance.get(`/social-accounts/client/${form.tenant_id}`);
        const rows = portal ? res.data?.data : res.data;
        if (!cancelled) setSocialAccounts(Array.isArray(rows) ? rows : []);
      } catch {
        if (!cancelled) setSocialAccounts([]);
      }
    };
    loadSocial();
    return () => { cancelled = true; };
  }, [portal, form.tenant_id]);

  const postsByDate = useMemo(() => {
    const map = {};
    for (const p of posts) {
      const key = p.Planned_Date;
      if (!map[key]) map[key] = [];
      map[key].push(p);
    }
    return map;
  }, [posts]);

  const tenantLabel = (t) => getTenantCompanyLabel(t);
  const tenantIdOf = (t) => t?.id ?? t?.client_id ?? t?.Customer_Id ?? t?.tenant_id ?? "";

  const clientOptions = useMemo(
    () =>
      [...tenants].sort((a, b) =>
        tenantLabel(a).localeCompare(tenantLabel(b), undefined, { sensitivity: "base" })
      ),
    [tenants]
  );

  const renderPartyOptions = () =>
    clientOptions.map((t) => (
      <option key={tenantIdOf(t)} value={tenantIdOf(t)}>
        {tenantLabel(t)}
      </option>
    ));

  const handleSave = async (e) => {
    e.preventDefault();
    if ((!portal && !form.tenant_id) || !form.Planned_Date) {
      alert(portal ? "Planned date is required." : "Client and planned date are required.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        Dm_Project_Id: form.Dm_Project_Id ? Number(form.Dm_Project_Id) : null,
        Social_Account_Id: form.Social_Account_Id ? Number(form.Social_Account_Id) : null,
        Planned_Date: form.Planned_Date,
        Planned_Time: form.Planned_Time || null,
        Content_Type: form.Content_Type,
        Caption: form.Caption || null,
        Media_URL: form.Media_URL || null,
        External_Link: form.External_Link || null,
        Assigned_To: form.Assigned_To ? Number(form.Assigned_To) : null,
        Status_Id: form.Status_Id ? Number(form.Status_Id) : null,
        Notes: form.Notes || null,
      };
      if (portal) {
        await portalDmApi.createCalendarPost(body);
      } else {
        await axiosInstance.post("/dm-posts", { ...body, tenant_id: Number(form.tenant_id) });
      }
      setShowForm(false);
      setForm(emptyForm(weekStartISO, portal));
      await load();
    } catch (err) {
      alert(errorMessage(err, "Failed to create post"));
    } finally {
      setSaving(false);
    }
  };

  const statusByName = (name) => statuses.find((s) => String(s.Status).toLowerCase() === name.toLowerCase());

  const actionsFor = (post) => {
    const list = STATUS_ACTIONS[String(post.Status || "").toLowerCase()] || DEFAULT_ACTIONS;
    return list.filter((a) => statusByName(a.status));
  };

  const quickStatus = async (post, statusName) => {
    const status = statusByName(statusName);
    if (!status) return;
    const payload = { Status_Id: status.Status_Id };
    if (statusName === "Posted") {
      const url = await askPrompt({
        title: "Mark as posted",
        label: "Live post link (optional)",
        type: "url",
        initial: post.Posted_URL || "",
        placeholder: "https://…",
        confirmLabel: "Mark posted",
      });
      if (url === null) return;
      if (url) payload.Posted_URL = url;
    }
    if (statusName === "Skipped") {
      const reason = await askPrompt({
        title: "Skip this post",
        label: "Why is it being skipped?",
        multiline: true,
        required: true,
        confirmLabel: "Skip post",
      });
      if (!reason) return;
      payload.Skip_Reason = reason;
    }
    setBusyPostId(post.Post_Id);
    setPostErrors(({ [post.Post_Id]: _cleared, ...rest }) => rest);
    try {
      if (portal) {
        await portalDmApi.updatePost(post.Post_Id, payload);
      } else {
        await axiosInstance.put(`/dm-posts/${post.Post_Id}`, payload);
      }
      setPosts((prev) =>
        prev.map((row) => (row.Post_Id === post.Post_Id ? { ...row, ...payload, Status: status.Status } : row))
      );
      await load({ quiet: true });
    } catch (err) {
      setPostErrors((prev) => ({ ...prev, [post.Post_Id]: errorMessage(err, "Could not update this post") }));
    } finally {
      setBusyPostId(null);
    }
  };

  const loadChangesRecipient = useCallback(
    (post) =>
      portal
        ? portalDmApi.changesRecipient(post.Post_Id)
        : axiosInstance.get(`/dm-posts/${post.Post_Id}/changes-recipient`).then((res) => res.data?.data),
    [portal]
  );

  const sendBack = async (post, body) => {
    const updated = portal
      ? await portalDmApi.requestPostChanges(post.Post_Id, body)
      : (await axiosInstance.post(`/dm-posts/${post.Post_Id}/request-changes`, body)).data?.data;
    setSendBackPost(null);
    if (updated) {
      setPosts((prev) => prev.map((row) => (row.Post_Id === post.Post_Id ? { ...row, ...updated } : row)));
    }
    await load({ quiet: true });
  };

  const runAction = (post, act) => {
    if (act === SEND_FOR_CHANGES) {
      setSendBackPost(post);
      return;
    }
    quickStatus(post, act.status);
  };

  const isCurrentWeek = weekStartISO === toISODateLocal(mondayOf(new Date()));
  const todayISO = toISODateLocal();

  return (
    <div className="myday-page">
      {promptUi}
      {sendBackPost ? (
        <SendBackDialog
          post={sendBackPost}
          users={users}
          loadRecipient={loadChangesRecipient}
          onCancel={() => setSendBackPost(null)}
          onSend={sendBack}
        />
      ) : null}
      {title ? (
        <div className="myday-toolbar dmcal-head">
          <div className="myday-toolbar__left">
            <h2 className="myday-title">{title}</h2>
            {subtitle ? <p className="myday-subtitle">{subtitle}</p> : null}
          </div>
        </div>
      ) : null}

      <div className="dmcal-bar">
        <div className="dmcal-week" role="group" aria-label="Week">
          <button
            type="button"
            className="dmcal-week__nav"
            onClick={() => setWeekStart(addDays(weekStart, -7))}
            aria-label="Previous week"
            title="Previous week"
          >
            <FaChevronLeft aria-hidden="true" />
          </button>
          <div className="dmcal-week__range">
            <FaRegCalendarAlt aria-hidden="true" />
            <span>{formatWeekRange(weekStart)}</span>
          </div>
          <button
            type="button"
            className="dmcal-week__nav"
            onClick={() => setWeekStart(addDays(weekStart, 7))}
            aria-label="Next week"
            title="Next week"
          >
            <FaChevronRight aria-hidden="true" />
          </button>
        </div>
        <div className="dmcal-bar__actions">
          <button
            type="button"
            className="myday-btn myday-btn--secondary dmcal-btn"
            onClick={() => setWeekStart(mondayOf(new Date()))}
            disabled={isCurrentWeek}
          >
            This week
          </button>
          <button
            type="button"
            className="myday-btn dmcal-btn"
            onClick={() => {
              if (!showForm && filterTenant) setForm((p) => (p.tenant_id ? p : { ...p, tenant_id: filterTenant }));
              setShowForm((v) => !v);
            }}
          >
            {showForm ? <FaTimes aria-hidden="true" /> : <FaPlus aria-hidden="true" />}
            <span>{showForm ? "Close" : "Add post"}</span>
          </button>
        </div>
      </div>

      <div className="dmcal-filters">
        {portal ? null : (
          <label className="dmcal-field">
            <span className="dmcal-field__label">Client</span>
            <select value={filterTenant} onChange={(e) => setFilterTenant(e.target.value)}>
              <option value="">Select a client…</option>
              {renderPartyOptions()}
            </select>
          </label>
        )}
        <label className="dmcal-field">
          <span className="dmcal-field__label">Project</span>
          <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)}>
            <option value="">All projects</option>
            {projectsForTenant(filterTenant).map((p) => (
              <option key={p.Dm_Project_Id} value={p.Dm_Project_Id}>
                {portal || filterTenant ? p.Project_Name : `${p.Project_Name} — ${p.Tenant_Name}`}
              </option>
            ))}
          </select>
        </label>
        {portal ? null : (
          <label className="dmcal-switch">
            <input
              type="checkbox"
              checked={includeInactiveTenants}
              onChange={(e) => setIncludeInactiveTenants(e.target.checked)}
            />
            <span className="dmcal-switch__track" aria-hidden="true" />
            <span className="dmcal-switch__text">Show inactive</span>
          </label>
        )}
      </div>

      {summary?.clients?.length ? (
        <div className="dm-summary">
          {summary.clients.map((c) => (
            <div key={c.tenant_id} className="dm-summary__card">
              <strong>{c.Client_Name}</strong>
              <span>{c.posted}/{c.planned} posted</span>
              <span>{c.open} open</span>
            </div>
          ))}
        </div>
      ) : null}

      {showForm ? (
        <form className="dm-form" onSubmit={handleSave}>
          <div className="dm-form__grid">
            {portal ? null : (
              <label>
                Client *
                <select
                  required
                  value={form.tenant_id}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, tenant_id: e.target.value, Dm_Project_Id: "", Social_Account_Id: "" }))
                  }
                >
                  <option value="">Select…</option>
                  {renderPartyOptions()}
                </select>
              </label>
            )}
            <label>
              DM project
              <select
                value={form.Dm_Project_Id}
                disabled={!portal && !form.tenant_id}
                onChange={(e) => setForm((p) => ({ ...p, Dm_Project_Id: e.target.value }))}
              >
                <option value="">None</option>
                {(portal || form.tenant_id ? projectsForTenant(form.tenant_id) : []).map((p) => (
                  <option key={p.Dm_Project_Id} value={p.Dm_Project_Id}>
                    {p.Project_Name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Social account
              <select
                value={form.Social_Account_Id}
                onChange={(e) => setForm((p) => ({ ...p, Social_Account_Id: e.target.value }))}
              >
                <option value="">Optional</option>
                {socialAccounts.map((a) => (
                  <option key={a.Account_Id} value={a.Account_Id}>
                    {a.Platform_Name} — {a.Account_Name || a.Username}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Planned date *
              <input
                type="date"
                required
                value={form.Planned_Date}
                onChange={(e) => setForm((p) => ({ ...p, Planned_Date: e.target.value }))}
              />
            </label>
            <label>
              Time
              <input
                type="time"
                value={form.Planned_Time}
                onChange={(e) => setForm((p) => ({ ...p, Planned_Time: e.target.value }))}
              />
            </label>
            <label>
              Content type
              <select
                value={form.Content_Type}
                onChange={(e) => setForm((p) => ({ ...p, Content_Type: e.target.value }))}
              >
                {CONTENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label>
              Status
              <select
                value={form.Status_Id}
                onChange={(e) => setForm((p) => ({ ...p, Status_Id: e.target.value }))}
              >
                {statuses.map((s) => (
                  <option key={s.Status_Id} value={s.Status_Id}>{s.Status}</option>
                ))}
              </select>
            </label>
            <label>
              Assign to
              <select
                value={form.Assigned_To}
                onChange={(e) => setForm((p) => ({ ...p, Assigned_To: e.target.value }))}
              >
                <option value="">{portal ? "Me" : "Unassigned"}</option>
                {users.map((u) => {
                  const id = u.user_id ?? u.User_Id;
                  const name = [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email || id;
                  return <option key={id} value={id}>{name}</option>;
                })}
              </select>
            </label>
            <label className="dm-form__full">
              Caption / text
              <textarea
                rows={3}
                value={form.Caption}
                onChange={(e) => setForm((p) => ({ ...p, Caption: e.target.value }))}
              />
            </label>
            <label>
              Media URL
              <span className="dmp-media-field">
                <input
                  type="url"
                  value={form.Media_URL}
                  onChange={(e) => setForm((p) => ({ ...p, Media_URL: e.target.value }))}
                />
                {form.Dm_Project_Id ? (
                  <button type="button" className="dmp-chip-btn" onClick={() => setPickingMedia((v) => !v)}>
                    <FaImages aria-hidden="true" /> Pick
                  </button>
                ) : null}
              </span>
            </label>
            {pickingMedia && form.Dm_Project_Id ? (
              <div className="dm-form__full">
                <MediaPicker
                  api={dmApi}
                  projectId={form.Dm_Project_Id}
                  selectedUrl={form.Media_URL}
                  onClose={() => setPickingMedia(false)}
                  onPick={(url) => {
                    setForm((p) => ({ ...p, Media_URL: url }));
                    setPickingMedia(false);
                  }}
                />
              </div>
            ) : null}
            <label>
              External link
              <input
                type="url"
                value={form.External_Link}
                onChange={(e) => setForm((p) => ({ ...p, External_Link: e.target.value }))}
              />
            </label>
          </div>
          <div className="dm-form__actions">
            <button type="submit" className="myday-btn" disabled={saving}>{saving ? "Saving…" : "Save post"}</button>
          </div>
        </form>
      ) : null}

      {!portal && !filterTenant ? (
        <div className="dmcal-empty">
          <div className="dmcal-empty__icon" aria-hidden="true">
            <FaRegCalendarAlt />
          </div>
          <div className="dmcal-empty__title">No client selected</div>
          <p className="dmcal-empty__text">Choose a client above to see and plan this week's social posts.</p>
        </div>
      ) : loading ? (
        <div className="myday-empty">Loading…</div>
      ) : error ? (
        <div className="myday-empty myday-empty--error">{error}</div>
      ) : (
        <div className="dm-week">
          {days.map((d) => {
            const key = toISODateLocal(d);
            const dayPosts = postsByDate[key] || [];
            const weekday = d.toLocaleDateString(undefined, { weekday: "short" });
            return (
              <div key={key} className={`dm-day${key === todayISO ? " dm-day--today" : ""}`}>
                <div className="dm-day__head">
                  <strong>{weekday}</strong>
                  <span>{key}</span>
                  <span className="dm-day__count">{dayPosts.length}</span>
                </div>
                <div className="dm-day__body">
                  {dayPosts.length === 0 ? (
                    <div className="dm-day__empty">No posts</div>
                  ) : (
                    dayPosts.map((p) => {
                      const platform = p.Platform || p.Platform_Name || "";
                      const currentStatus = String(p.Status || "").toLowerCase();
                      const [primary, ...more] = actionsFor(p);
                      const busy = busyPostId === p.Post_Id;
                      const changeRequest = currentStatus === "draft" ? latestChangeRequest(p.Notes) : null;
                      const showProject =
                        p.Project_Name &&
                        String(p.Project_Name).trim().toLowerCase() !== String(p.Client_Name || "").trim().toLowerCase();
                      return (
                        <div
                          key={p.Post_Id}
                          className="dm-post-chip"
                          style={{ "--dm-platform": platformColor(platform) }}
                        >
                          <div className="dm-post-chip__top">
                            <span className="dm-post-chip__platform">
                              <span className="dm-post-chip__dot" aria-hidden="true" />
                              {platform || "No platform"}
                              {p.Content_Type ? <span className="dm-post-chip__ctype">· {p.Content_Type}</span> : null}
                            </span>
                            <span className={`myday-pill ${statusClass(p.Status)}`}>{p.Status || "—"}</span>
                          </div>
                          <div className="dm-post-chip__client">
                            {p.Client_Name}
                            {showProject ? <span className="dm-post-chip__project"> · {p.Project_Name}</span> : null}
                          </div>
                          <div className="dm-post-chip__caption">{p.Caption || "Untitled"}</div>
                          {changeRequest ? (
                            <div className="dm-post-chip__changes" title={`Requested by ${changeRequest.by}`}>
                              <strong>
                                Changes{p.Assigned_To_Name ? ` · with ${p.Assigned_To_Name}` : ""}
                              </strong>
                              <span>{changeRequest.note}</span>
                            </div>
                          ) : null}
                          {currentStatus === "skipped" && p.Skip_Reason ? (
                            <div className="dm-post-chip__note">Skipped: {p.Skip_Reason}</div>
                          ) : null}
                          {currentStatus === "posted" ? (
                            p.Posted_URL ? (
                              <a className="dm-post-chip__live" href={p.Posted_URL} target="_blank" rel="noreferrer">
                                <FaExternalLinkAlt aria-hidden="true" /> View live post
                              </a>
                            ) : (
                              <div className="dm-post-chip__live dm-post-chip__live--plain">
                                <FaCheck aria-hidden="true" /> Posted
                              </div>
                            )
                          ) : null}
                          {primary ? (
                            <div className="dm-post-chip__actions" role="group" aria-label="Update status">
                              <button
                                type="button"
                                className={`dm-act dm-act--main${primary.tone ? ` dm-act--${primary.tone}` : ""}`}
                                disabled={busy}
                                onClick={() => runAction(p, primary)}
                              >
                                <primary.Icon aria-hidden="true" />
                                <span>{busy ? "Saving…" : primary.label}</span>
                              </button>
                              {more.map((a) => (
                                <button
                                  key={a.status}
                                  type="button"
                                  className={`dm-act dm-act--sub${a.tone ? ` dm-act--${a.tone}` : ""}`}
                                  disabled={busy}
                                  onClick={() => runAction(p, a)}
                                  title={a.label}
                                  aria-label={a.label}
                                >
                                  <a.Icon aria-hidden="true" />
                                  <span>{a.short}</span>
                                </button>
                              ))}
                            </div>
                          ) : null}
                          {postErrors[p.Post_Id] ? (
                            <div className="dm-post-chip__error" role="alert">{postErrors[p.Post_Id]}</div>
                          ) : null}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default DMCalendar;
